import { Injectable, inject } from '@angular/core';
import { Form, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MAX_FILE_SIZE } from '../constants/Values';
import { AlertNotificationService } from './alert-notification.service';
import { FormInputConfig } from '../interfaces/form-input-config';
import { Subject } from 'rxjs';
import { ToastService } from '../components/toasters/Toast.service';
import { Logger } from '../../../common/core/utilities/logging/logger';

@Injectable()
export class FileUploadService {
  private toastService: ToastService = inject(ToastService);
  private alertNotification: AlertNotificationService = inject(
    AlertNotificationService
  );
  private translateService: TranslateService = inject(TranslateService);
  resetSub: Subject<boolean> = new Subject();

  private toBase64(
    file: File,
    shouldResize: boolean = false,
    dimensions: { w: number; h: number } = { w: 250, h: 250 }
  ) {
    Logger.debug('toBase64', file);
    return new Promise((resolve, reject) => {
      // Check if the file is an image
      if (!file.type.startsWith('image/')) {
        return reject(new Error('The provided file is not an image.'));
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        if (shouldResize) {
          this.resizeImage(base64String, dimensions)
            .then((resizedImage) => {
              resolve(resizedImage);
            })
            .catch((error) => {
              reject(error);
            });
        } else {
          resolve(base64String);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  private resizeImage(
    imageURL: any,
    dimensions: { w: number; h: number } = { w: 200, h: 200 }
  ): Promise<any> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = dimensions.w;
        canvas.height = dimensions.h;
        const ctx = canvas.getContext('2d');
        if (ctx != null) {
          ctx.drawImage(image, 0, 0, dimensions.w, dimensions.h);
        }
        var data = canvas.toDataURL('image/jpeg', 1);
        resolve(data);
      };
      image.src = imageURL;
    });
  }

  private isFileSizeValid(file: File, formItem: FormInputConfig): boolean {
    if (file?.size > MAX_FILE_SIZE) {
      return false;
    }
    return true;
  }

  private isFileTypeValid(file: File, formItem: FormInputConfig): boolean {
    Logger.debug('isFileTypeValid', file);
    const type = `${file?.name.split(/\./g).pop()}`.toLowerCase();
    Logger.debug(
      !formItem.fileConfig?.allowedExtension ||
        formItem.fileConfig.allowedExtension.includes(type)
    );
    return (
      !formItem.fileConfig?.allowedExtension ||
      formItem.fileConfig.allowedExtension.includes(type)
    );
  }

  private async processFile(
    file: File,
    formItem: FormInputConfig
  ): Promise<any> {
    const result = formItem.fileConfig?.keepFile
      ? file
      : await this.toBase64(
          file,
          formItem.fileConfig?.resize,
          formItem.fileConfig?.dimensions
        );
    const type = `${file.name.split(/\./g).pop()}`.toLowerCase();
    Logger.debug('type', type);
    return { [formItem.fileConfig?.keyName || 'image']: result, type };
  }

  private updateFormFiles(
    files: any,
    formItem: FormInputConfig,
    formGroup: FormGroup
  ): void {
    formGroup.get(formItem.name)?.setValue(files);
  }

  private resetField(
    formItem: FormInputConfig,
    formGroup: FormGroup,
    message?: string
  ): void {
    formGroup.get(formItem.name)?.reset();
    this.resetSub.next(true);
    if (message) {
      this.toastService.showError(
        'Error',
        this.translateService.instant(message)
      );
    }
  }

  async handleFileUpload(
    files: FileList | File[],
    formItem: FormInputConfig,
    formGroup: FormGroup
  ): Promise<boolean> {
    Logger.debug('handleFileUpload', files, formItem, formGroup);
    try {
      const count = formItem.maxValue - formItem.minValue + 1;
      const fileArray = Array.from(files);
      if (count === 1) {
        const file = fileArray[0];
        if (!this.isFileSizeValid(file, formItem)) {
          this.resetField(
            formItem,
            formGroup,
            'please select file less than 2 megabytes'
          );
          return false;
        }
        if (!this.isFileTypeValid(file, formItem)) {
          this.resetField(formItem, formGroup, 'Please select valid file');
          return false;
        }
        const processedFile = await this.processFile(file, formItem);
        this.updateFormFiles(
          { ...processedFile, type: processedFile.type },
          formItem,
          formGroup
        );
      } else {
        const processedFiles = [];
        for (const file of fileArray.slice(0, count)) {
          if (!this.isFileSizeValid(file, formItem)) {
            this.resetField(
              formItem,
              formGroup,
              'please select file less than 2 megabytes'
            );
            return false;
          }
          if (!this.isFileTypeValid(file, formItem)) {
            this.resetField(formItem, formGroup, 'Please select valid file');
            return false;
          }
          const processedFile = await this.processFile(file, formItem);
          processedFiles.push(processedFile);
        }
        this.updateFormFiles(processedFiles, formItem, formGroup);
      }

      return true;
    } catch (error) {
      return false;
    }
  }
}
